import { getTicketPdfAmount } from "../../utility/ticketPdfPricing";
import { Page, View, Image, Text, StyleSheet } from "@react-pdf/renderer";
import comittyLogo from "../logos/logo.png";

const styles = StyleSheet.create({
  page: { padding: 26, backgroundColor: "#f4f6fb", fontFamily: "Poppins", color: "#17213d" },
  // A4 is 841.89 pt high. With the page padding, the card must stay within 789 pt.
  card: { height: 789, backgroundColor: "#ffffff", borderWidth: 1, borderColor: "#dce4f3", overflow: "hidden" },
  header: { height: 145, padding: 20, backgroundColor: "#142240", overflow: "hidden" },
  brandRow: { width: "100%", flexDirection: "column", alignItems: "center" },
  brandLogo: { width: 420, height: 128, objectFit: "contain" },
  brand: { color: "#d9e6ff", fontSize: 10, fontFamily: "Poppins Bold", marginTop: 6 },
  eventName: { color: "#ffffff", fontSize: 23, lineHeight: 27, fontFamily: "Poppins Bold", marginTop: 8 },
  description: { color: "#d9e6ff", fontSize: 9, lineHeight: 13, marginTop: 8 },
  content: { flexDirection: "row", height: 596, padding: 20, overflow: "hidden" },
  qrColumn: { width: 170, paddingRight: 18, alignItems: "center" },
  qrLabel: { color: "#6f4cff", fontSize: 9, fontFamily: "Poppins Bold", marginBottom: 10 },
  qr: { width: 135, height: 135 },
  qrHelp: { color: "#62708c", fontSize: 8, lineHeight: 12, marginTop: 10, textAlign: "center" },
  participantId: { color: "#62708c", fontSize: 7, marginTop: 7, textAlign: "center" },
  detailsColumn: { flex: 1, paddingLeft: 4, position: "relative" },
  sectionTitle: { color: "#17213d", fontSize: 13, fontFamily: "Poppins Bold", marginBottom: 8 },
  ticketRow: { flexDirection: "row", paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: "#dce4f3" },
  ticketName: { flex: 1, color: "#17213d", fontSize: 10 },
  ticketQuantity: { width: 48, color: "#62708c", fontSize: 9 },
  ticketAmount: { width: 54, textAlign: "right", color: "#17213d", fontSize: 9, fontFamily: "Poppins Bold" },
  participantBox: { position: "absolute", left: 4, right: 0, backgroundColor: "#f4f6fb", overflow: "hidden" },
  participantTitle: { position: "absolute", top: 12, left: 12, color: "#6f4cff", fontSize: 10, fontFamily: "Poppins Bold" },
  participantLine: { position: "absolute", left: 12, right: 12, color: "#35405c", fontSize: 8.5, lineHeight: 11 },
  footer: { height: 48, padding: 14, borderTopWidth: 1, borderTopColor: "#dce4f3", flexDirection: "row" },
  footerText: { flex: 1, color: "#62708c", fontSize: 8 },
  footerWebsite: { color: "#62708c", fontSize: 8, textAlign: "right" },
});

const labelsByLanguage = {
  fr: { ticket: "BILLET COMITTY", thanks: "Merci pour votre achat", tickets: "Vos billets", participant: "Participant", scan: "Présentez ce QR code à l'entrée", paper: "La version numérique suffit.", quantity: "Qté" },
  nl: { ticket: "COMITTY TICKET", thanks: "Bedankt voor uw aankoop", tickets: "Uw tickets", participant: "Deelnemer", scan: "Toon deze QR-code aan de ingang", paper: "De digitale versie is voldoende.", quantity: "Aantal" },
  en: { ticket: "COMITTY TICKET", thanks: "Thank you for your purchase", tickets: "Your tickets", participant: "Participant", scan: "Present this QR code at the entrance", paper: "The digital version is sufficient.", quantity: "Qty" },
};

const textValue = (value) => (Array.isArray(value) ? value.filter(Boolean).join(", ") : String(value || ""));
const amount = (value) => `${Number(value || 0).toFixed(2)} €`;

const DefaultTicket = (props) => {
  const labels = labelsByLanguage[props.lang] || labelsByLanguage.en;
  const participantDetails = (props.details || [])
    .filter((detail) => textValue(detail?.value))
    .slice(0, 12);
  const tickets = (props.tickets || []).slice(0, 8);

  return (
    <Page size="A4" style={styles.page} wrap={false}>
      <View style={styles.card} wrap={false}>
        <View style={styles.header} wrap={false}>
          <View style={styles.brandRow}>
            <Image src={comittyLogo} style={styles.brandLogo} />
            <Text style={styles.brand}>{labels.ticket}</Text>
          </View>
          <Text style={styles.eventName} maxLines={2}>{String(props.eventName || "Event").slice(0, 100)}</Text>
          <Text style={styles.description}>{labels.thanks}</Text>
          {props.eventDescription ? <Text style={styles.description} maxLines={3}>{String(props.eventDescription).slice(0, 220)}</Text> : null}
        </View>
        <View style={styles.content} wrap={false}>
          <View style={styles.qrColumn}>
            <Text style={styles.qrLabel}>COMITTY</Text>
            {props.qrcode ? <Image src={props.qrcode} style={styles.qr} /> : null}
            <Text style={styles.qrHelp}>{labels.scan}</Text>
            {props.participantId ? <Text style={styles.participantId}>{props.participantId}</Text> : null}
          </View>
          <View style={styles.detailsColumn}>
            <Text style={styles.sectionTitle}>{labels.tickets}</Text>
            {tickets.map((ticket, index) => (
              <View style={styles.ticketRow} key={`${ticket?.ticketId || ticket?.name || "ticket"}-${index}`}>
                <Text style={styles.ticketName}>{ticket?.name || "Ticket"}</Text>
                <Text style={styles.ticketQuantity}>{labels.quantity} {ticket?.number || 1}</Text>
                <Text style={styles.ticketAmount}>{amount(getTicketPdfAmount(ticket))}</Text>
              </View>
            ))}
            {participantDetails.length ? (
              <View
                style={[
                  styles.participantBox,
                  { top: 44 + tickets.length * 29, height: 34 + participantDetails.length * 13 },
                ]}
                wrap={false}
              >
                <Text style={styles.participantTitle}>{labels.participant}</Text>
                {participantDetails.map((detail, index) => (
                  <Text
                    key={`${detail?.name || "detail"}-${index}`}
                    style={[styles.participantLine, { top: 32 + index * 13 }]}
                    maxLines={1}
                  >
                    {detail.name}: {textValue(detail.value)}
                  </Text>
                ))}
              </View>
            ) : null}
          </View>
        </View>
        <View style={styles.footer} wrap={false}>
          <Text style={styles.footerText}>{labels.paper}</Text>
          <Text style={styles.footerWebsite}>comitty.be</Text>
        </View>
      </View>
    </Page>
  );
};

export const ResumeFr = (props) => <DefaultTicket {...props} lang="fr" />;
export const ResumeNl = (props) => <DefaultTicket {...props} lang="nl" />;
export const ResumeEn = (props) => <DefaultTicket {...props} lang="en" />;
